import React, {useMemo, useState} from "react";
import {useApiRequest} from "../../../../common/hooks/useApiRequest";
import {Alert, Box, CircularProgress, IconButton, Typography} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestoreIcon from '@mui/icons-material/Restore';
import {getAudioForSource} from "../../../../common/utils/audioUtils";

import {
    CreateAudioConfig,
    UpdateAudioConfig,
} from "../../types/word_operations";
import {useDeck} from "../../../deck-management/context/DeckContext";
import {useAudio} from "../../../../common/context/AudioContext";


export enum AudioType {
    Word, Sentence
}

export interface AudioParamsViewProps {
    type: AudioType
    existingAudioPath?: string | null
    text: string
    audioConfig: CreateAudioConfig | UpdateAudioConfig
    onChange: (newConfig : CreateAudioConfig | UpdateAudioConfig) => void
    isLoading: boolean
}


export const AudioParamsView = ({type, existingAudioPath, text, audioConfig, onChange, isLoading}: AudioParamsViewProps) => {

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string | null>(null);
    const [isGenerationPending, setIsGenerationPending] = useState<boolean>(isLoading);
    const [isUploadPending, setIsUploadPending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const {currentDeck} = useDeck();

    const {postRequest} = useApiRequest();

    const {play} = useAudio();

    const urlToPlay = useMemo(() => {
        if (audioConfig === null) {
            return existingAudioPath || null;
        } else if (audioConfig.audio_b64) {
            return uploadedAudioUrl;
        } else if (audioConfig.fromPath) {
            return audioConfig.fromPath;
        }
        return null;
    }, [audioConfig, existingAudioPath, uploadedAudioUrl]);

    const audioDisplayString = useMemo(() => {
        if (audioConfig === null)
            return existingAudioPath ? "existing audio" : existingAudioPath === null ? "audio load error" : "no audio";
        if (audioConfig.audio_b64)
            return uploadedFile?.name ? (uploadedFile.name.length < 13 ? uploadedFile.name : `${uploadedFile.name.substring(0, 10)}...`) : "uploaded audio";
        if (audioConfig.fromPath)
            return "audio generated";
        if ('delete' in audioConfig && audioConfig.delete)
            return "audio deleted";
    }, [audioConfig, existingAudioPath, uploadedAudioUrl]);



    const generateAudio = () => {
        setIsGenerationPending(true);
        postRequest<{audioPath: string} | {exampleSentences: {audioPath: string}[]}>('word-properties/generate/single', {
            words: type === AudioType.Word ? {word: text} : {sentence: text},
            sourceLanguage: currentDeck!.language,
            fieldSelection: type === AudioType.Word ? { audio: true } : {sentenceAudio: true},
        })
            .then((result) => {
                const audioPath = type === AudioType.Word
                    ? (result as {audioPath: string}).audioPath
                    : (result as {exampleSentences: {audioPath: string}[]}).exampleSentences[0].audioPath;
                setError(null);
                onChange({fromPath: audioPath});
        })
            .catch(() => setError("Failed to generate audio."))
            .finally(() => setIsGenerationPending(false));
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const f = event.target.files?.[0] || null;

        if (f && f.type !== "audio/mpeg") {
            setError("Invalid file type. Please upload an MP3 file.");
            return;
        }

        if (f) {
            setIsUploadPending(true);
            try {
                const base64 = await toBase64(f);
                setError(null);
                setUploadedFile(f);
                setUploadedAudioUrl(URL.createObjectURL(f));
                onChange({audio_b64: base64});
            } catch {
                setError("Failed to upload file.");
            } finally {
                setIsUploadPending(false);
            }
        }
    };

    const deleteAudio = () => {
        setError(null);
        onChange(existingAudioPath ? { delete: true } : null);
    }

    const restoreAudio = () => {
        setError(null);
        onChange(null);
    }

    const toBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);

            reader.readAsDataURL(file);
        });
    };


    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <IconButton
                            title="Play Audio"
                            size={"small"}
                            onClick={() => {
                                const audio = getAudioForSource(urlToPlay);
                                if (audio) {
                                    play(audio);
                                }
                            }}
                            disabled={!urlToPlay}
                            sx={{ backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }}
                        >
                            <PlayArrowIcon />
                        </IconButton>
                        <Typography variant="body2"
                                    fontStyle={audioConfig === null && existingAudioPath === null ? "italic" : "normal"}
                                    color={audioConfig === null && existingAudioPath === null ? "text.secondary" : "text.primary"}
                        >
                            {audioDisplayString}
                        </Typography>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <IconButton
                        title="Generate Audio"
                        size={"small"}
                        onClick={generateAudio}
                        disabled={!text || isGenerationPending || isLoading}
                        sx={{ backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }}
                    >
                        {isGenerationPending || isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <AutoAwesomeIcon />
                        )}
                    </IconButton>
                    <IconButton
                        title="Upload Audio"
                        size={"small"}
                        component="label"
                        disabled={!text || isUploadPending || isLoading}
                        sx={{ backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }}
                    >
                        {isUploadPending ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <FileUploadIcon />
                        )}
                        <input
                            type="file"
                            accept="audio/mpeg"
                            hidden
                            onChange={handleFileChange}
                        />
                    </IconButton>
                    {((!!audioConfig?.audio_b64 || !!audioConfig?.fromPath) && !!existingAudioPath) ? (
                        <IconButton
                            title="Restore Audio"
                            size={"small"}
                            onClick={restoreAudio}
                            sx={{ backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }}
                        >
                            <RestoreIcon />
                        </IconButton>
                    ) : (
                        <IconButton
                            title="Delete Audio"
                            size={"small"}
                            onClick={deleteAudio}
                            disabled={!urlToPlay || isLoading || isGenerationPending || isUploadPending}
                            sx={{ backgroundColor: "primary.main", color: "black", "&:hover": { backgroundColor: "primary.dark" } }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </div>
                </Box>
            </div>
            {error && (
                <Alert severity="error" style={{ marginTop: "1rem" }}>
                    {error}
                </Alert>
            )}
        </div>
    )


}